import React, { useState, useEffect, useCallback } from 'react';
import { partnersAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import { RefreshCw, Plus, Trash2, Handshake, Pencil, X } from 'lucide-react';
import '../Admin/AdminDashboard.css';

const TIERS = ['candidate', 'tier3', 'tier2', 'tier1', 'suspended', 'retired'];
const QUAL = ['identified', 'screened', 'trial_issued', 'inspected', 'matrix_documented', 'framework_signed', 'production_approved', 'suspended'];

const emptyForm = () => ({
  name: '',
  legalName: '',
  country: 'MT',
  tier: 'candidate',
  processes: '',
  materials: '',
  certifications: '',
  insuranceOnFile: false,
  qualificationStatus: 'identified',
  notes: '',
});

const AdminPartners = () => {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partnersAPI.getAll({ limit: 100, tier: tierFilter || undefined });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, [tierFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (row) => {
    setEditingId(row._id);
    setForm({
      name: row.name || '',
      legalName: row.legalName || '',
      country: row.country || '',
      tier: row.tier || 'candidate',
      processes: (row.processes || []).join(', '),
      materials: (row.materials || []).join(', '),
      certifications: (row.certifications || []).join(', '),
      insuranceOnFile: Boolean(row.insuranceOnFile),
      qualificationStatus: row.qualificationStatus || 'identified',
      notes: row.notes || '',
    });
    setShowForm(true);
  };

  const splitList = (s) => (s ? s.split(',').map((x) => x.trim()).filter(Boolean) : []);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    const body = {
      name: form.name,
      legalName: form.legalName,
      country: form.country,
      tier: form.tier,
      processes: splitList(form.processes),
      materials: splitList(form.materials),
      certifications: splitList(form.certifications),
      insuranceOnFile: form.insuranceOnFile,
      qualificationStatus: form.qualificationStatus,
      notes: form.notes,
    };
    try {
      if (editingId) {
        await partnersAPI.update(editingId, body);
        toast.success('Partner updated');
      } else {
        await partnersAPI.create(body);
        toast.success('Partner created');
      }
      setShowForm(false);
      load();
    } catch {
      toast.error('Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this partner?')) return;
    try {
      await partnersAPI.delete(id);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content">
        <div className="content-header">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Handshake size={28} color="#22c55e" /> Partners
            </h1>
            <p style={{ color: '#a1a1aa' }}>
              Manufacturing network (qualification protocol).{total ? ` ${total} partners.` : ' Empty — not agri/referral partners.'}
            </p>
          </div>
          <div className="admin-header-right">
            <button onClick={openCreate} style={btn('#22c55e')}><Plus size={15} /> New partner</button>
            <button className="btn-secondary" onClick={load}><RefreshCw size={16} /></button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <Chip active={!tierFilter} onClick={() => setTierFilter('')} label="All tiers" />
          {TIERS.map((t) => (
            <Chip key={t} active={tierFilter === t} onClick={() => setTierFilter(t)} label={t} />
          ))}
        </div>

        {showForm && (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: '#fff' }}>{editingId ? 'Edit partner' : 'New partner'}</h3>
              <button onClick={() => setShowForm(false)} style={iconBtn}><X size={16} /></button>
            </div>
            <div style={grid}>
              <label style={field}><span style={muted}>Name *</span><input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>Legal name</span><input value={form.legalName} onChange={(e) => setForm((p) => ({ ...p, legalName: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>Country</span><input value={form.country} onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))} style={input} /></label>
              <label style={field}>
                <span style={muted}>Tier</span>
                <select value={form.tier} onChange={(e) => setForm((p) => ({ ...p, tier: e.target.value }))} style={input}>
                  {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label style={field}>
                <span style={muted}>Qualification status</span>
                <select value={form.qualificationStatus} onChange={(e) => setForm((p) => ({ ...p, qualificationStatus: e.target.value }))} style={input}>
                  {QUAL.map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </label>
              <label style={field}><span style={muted}>Processes (comma)</span><input value={form.processes} onChange={(e) => setForm((p) => ({ ...p, processes: e.target.value }))} placeholder="fdm, sla, mjf…" style={input} /></label>
              <label style={field}><span style={muted}>Materials (comma)</span><input value={form.materials} onChange={(e) => setForm((p) => ({ ...p, materials: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>Certifications (comma)</span><input value={form.certifications} onChange={(e) => setForm((p) => ({ ...p, certifications: e.target.value }))} style={input} /></label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#a1a1aa', fontSize: 13 }}>
                <input type="checkbox" checked={form.insuranceOnFile} onChange={(e) => setForm((p) => ({ ...p, insuranceOnFile: e.target.checked }))} />
                Insurance on file
              </label>
              <label style={{ ...field, gridColumn: '1 / -1' }}><span style={muted}>Notes</span><textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={2} style={input} /></label>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={handleSave} style={btn('#22c55e')}>Save</button>
              <button onClick={() => setShowForm(false)} style={ghost}>Cancel</button>
            </div>
          </div>
        )}

        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr style={thead}>
                {['Name', 'Country', 'Tier', 'Qualification', 'Processes', 'Insurance', ''].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={empty}>Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} style={empty}>
                  No manufacturing partners yet. Add candidates from the Partner Qualification Protocol.
                </td></tr>
              ) : items.map((row) => (
                <tr key={row._id} style={rowStyle}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{row.name}</div>
                    <div style={{ color: '#71717a', fontSize: 12 }}>{row.legalName || ''}</div>
                  </td>
                  <td style={td}>{row.country || '—'}</td>
                  <td style={td}><span style={badge('#22c55e')}>{row.tier}</span></td>
                  <td style={td}>{row.qualificationStatus}</td>
                  <td style={{ ...td, color: '#a1a1aa', fontSize: 12 }}>{(row.processes || []).join(', ') || '—'}</td>
                  <td style={td}>{row.insuranceOnFile ? 'Yes' : 'No'}</td>
                  <td style={td}>
                    <button onClick={() => openEdit(row)} style={iconBtn}><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(row._id)} style={iconBtn}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

function Chip({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
      border: active ? '2px solid #22c55e' : '1px solid #27272a',
      background: active ? '#22c55e20' : '#18181b',
      color: active ? '#86efac' : '#a1a1aa',
      fontWeight: active ? 600 : 400,
    }}>{label}</button>
  );
}

const btn = (c) => ({ background: c, border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 });
const ghost = { background: '#27272a', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#a1a1aa', cursor: 'pointer' };
const iconBtn = { background: 'transparent', border: 'none', color: '#a1a1aa', cursor: 'pointer', padding: 6 };
const card = { background: '#18181b', borderRadius: 12, border: '1px solid #27272a', padding: 24, marginBottom: 24 };
const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const field = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 };
const muted = { color: '#71717a' };
const input = { background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 };
const tableWrap = { background: '#111', borderRadius: 12, overflow: 'hidden', border: '1px solid #27272a' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const thead = { background: '#18181b', borderBottom: '1px solid #27272a' };
const th = { padding: '12px 16px', color: '#71717a', fontWeight: 600, textAlign: 'left' };
const td = { padding: '14px 16px', color: '#fff' };
const rowStyle = { borderBottom: '1px solid #1c1c1c' };
const empty = { padding: 40, textAlign: 'center', color: '#71717a' };
const badge = (c) => ({ background: `${c}20`, color: c, borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 600 });

export default AdminPartners;
